import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, take, tap, throwError } from 'rxjs';
import { v4 } from 'uuid';

export interface ILoader<TTranslationKey extends string = string> {
  id?: string,
  subtle?: boolean,
  text?: TTranslationKey,
  key?: string
}

@Injectable({ providedIn: 'root' })
export class LoaderService<TTranslationKey extends string = string> {

  readonly loadersSubject = new BehaviorSubject<{ [id: string]: ILoader }>({})
  get loaders() {
    return this.loadersSubject.value;
  }

  constructor() {

  }

  show(params?: ILoader<TTranslationKey> & { until?: Observable<any> }): string {

    const { subtle, key, text, until } = params || {};
    const id = params?.id || v4();

    const newLoaders = { ...this.loaders };
    newLoaders[id] = {
      subtle,
      text,
      key
    }

    if (until) {
      until.pipe(take(1))
        .subscribe(() => {
          this.hide(id);
        })
    }

    this.loadersSubject.next(newLoaders);
    return id;
  }

  hide(id?: string) {
    if (!id) {
      this.loadersSubject.next({});
      return;
    }

    const newLoaders = { ...this.loaders };
    delete newLoaders[id];
    this.loadersSubject.next(newLoaders);
  }

  hideByKey(key: string) {

    const ids = Object.values(this.loaders)
      .filter((loader) => loader.key === key)
      .map(({ id }) => id);

    ids.forEach((id) => {
      this.hide(id);
    })
  }

  readonly disposeLoader = (id: string) => <T extends any>(obs: Observable<T>) =>
    obs.pipe(
      tap(() => this.hide(id)),
      catchError((error) => {
        this.hide(id);
        return throwError(() => error)
      }),
    )

  readonly disposeByKey = (key: string) => <T extends any>(obs: Observable<T>) =>
    obs.pipe(
      tap({
        complete: () => {
          this.hideByKey(key)
        },
        error: () => {
          this.hideByKey(key)
        },
        next: () => {
          this.hideByKey(key)
        },
        finalize: () => {
          this.hideByKey(key)
        }
      })
    )
}
